import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Edit, Share2, Archive, Trash2, Flag, MessageSquare } from 'lucide-react';
import { BiMoney } from 'react-icons/bi';

const OngoingProjectCard = ({ orders = [], isLoading = false }) => {
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const dropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const dropdownOptions = [
    { icon: Edit, label: "Edit Project", action: () => console.log("Edit") },
    { icon: Share2, label: "Share Project", action: () => console.log("Share") },
    { icon: Flag, label: "Mark as Priority", action: () => console.log("Priority") },
    { icon: Archive, label: "Archive Project", action: () => console.log("Archive") },
    { icon: Trash2, label: "Delete Project", action: () => console.log("Delete"), danger: true }
  ];

  // Determine active project info for animation hook safely
  const activeOrder = orders?.[0];

  // Calculate progress based on dates
  const progressAmount = React.useMemo(() => {
    if (!activeOrder) return 0;
    if (activeOrder.status === 'completed') return 100;
    if (activeOrder.status === 'cancelled') return 0;

    const start = new Date(activeOrder.createdAt).getTime();
    const end = activeOrder.deliveryDate ? new Date(activeOrder.deliveryDate).getTime() : null;
    const now = new Date().getTime();

    if (!end || end <= start) {
      // Fallback if no delivery date or invalid range
      return activeOrder.status === 'in-progress' ? 40 : 15;
    }

    const total = end - start;
    const elapsed = now - start;
    const percentage = Math.floor((elapsed / total) * 100);

    return Math.min(100, Math.max(0, percentage));
  }, [activeOrder]);

  // Reusing the same animation logic unconditionally
  useEffect(() => {
    if (!activeOrder) return;
    
    setAnimatedProgress(0); // reset when order changes
    const timer = setTimeout(() => {
      const animate = () => {
        setAnimatedProgress(prev => {
          if (prev >= progressAmount) return progressAmount;
          return prev + 2;
        });
      };
      const interval = setInterval(animate, 20);
      setTimeout(() => clearInterval(interval), progressAmount * 20);
    }, 300);
    return () => clearTimeout(timer);
  }, [progressAmount, activeOrder?._id]);

  if (isLoading) {
    return (
      <div className="w-full mx-auto bg-white dark:bg-[#1d2026] rounded-[10px] overflow-hidden p-6 animate-pulse">
        <div className="h-6 bg-gray-200 dark:bg-[#32353c] rounded w-1/2 mb-4"></div>
        <div className="h-32 bg-gray-200 dark:bg-[#32353c] rounded w-full mb-4"></div>
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 dark:bg-[#32353c] rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 dark:bg-[#32353c] rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (!orders || orders.length === 0) {
    return (
      <div className="w-full mx-auto bg-white dark:bg-[#1d2026] rounded-[10px] overflow-hidden p-6 text-center border border-gray-100 dark:border-[#32353c]">
        <h3 className="text-lg font-medium md:font-semibold text-gray-900 dark:text-white mb-2">Ongoing project</h3>
        <p className="text-gray-500 dark:text-gray-400 text-sm">You have no active ongoing projects at the moment.</p>
      </div>
    );
  }
  // Helper to color-code statuses
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800';
      case 'in-progress':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800';
      case 'pending':
        return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-800';
      case 'cancelled':
      case 'disputed':
        return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800';
      default:
        return 'bg-gray-100 dark:bg-[#0b0e14]/50 text-gray-700 dark:text-gray-400 border border-gray-200 dark:border-[#32353c]';
    }
  };

  return (
    <div className="w-full mx-auto bg-white dark:bg-[#1d2026] rounded-[10px] overflow-hidden shadow-sm border border-gray-100 dark:border-[#32353c]">
      {/* Header */}
      <div className="w-full flex items-center justify-between p-4 pb-0">
        <h3 className="text-lg font-medium md:font-semibold text-gray-900 dark:text-white">Ongoing project</h3>
      
      </div>

      {/* Project Image */}
      <div className="px-4 pt-4">
        <div className="relative overflow-hidden rounded-xl">
          <img
            src={activeOrder.img || activeOrder.gigId?.cover}
            alt={activeOrder.title}
            className="w-full h-32 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
        </div>
      </div>

      {/* Project Details */}
      <div className="p-4">
        {/* Title and Category */}
        <div className="mb-4">
          <div className="flex justify-between items-start gap-2 mb-1">
            <h4 className="font-medium text-[14px] text-gray-900 dark:text-white text-base truncate">
              {activeOrder.title}
            </h4>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium capitalize flex-shrink-0 ${getStatusColor(activeOrder.status)}`}>
              {activeOrder.status}
            </span>
          </div>
          <p className="text-[12px] font-light text-gray-600 dark:text-gray-400 capitalize">
            {activeOrder.gigCategory?.[0] || 'Project'} • <span className="text-[#174568] dark:text-blue-400 font-medium">{activeOrder.pricingPackage}</span>
          </p>
        </div>

        {/* Progress Section */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Progress</span>
            <span className="text-sm font-semibold text-[#7295B8]">{animatedProgress}%</span>
          </div>
          
          {/* Animated Progress Bar */}
          <div className="relative w-full bg-[#BAC8FF] dark:bg-gray-700 rounded-full h-2 overflow-hidden">
            <div 
              className="absolute left-0 top-0 h-full bg-[#7295B8] rounded-full transition-all duration-300 ease-out"
              style={{ width: `${animatedProgress}%` }}
            >
              <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* Payment Amount */}
        <div className="flex items-center gap-2 mb-3">
          <BiMoney className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <span className="text-sm text-gray-600 dark:text-gray-300 font-medium">${activeOrder.price?.toFixed(2)}</span>
        </div>

     

        {/* Send Message Button */}
        <button 
          onClick={() => activeOrder?.chatId && navigate(`/clients/messages/${activeOrder.chatId._id || activeOrder.chatId}`)}
          className="w-full bg-[#174568] hover:bg-[#1d2026] text-white font-normal md:font-medium text-[12px] md:text-[15px] py-1.5 px-2 rounded-[5px] transition-colors duration-200 flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
        >
          <MessageSquare className="w-3 h-3 md:w-4 md:h-4" />
          Message
        </button>
      </div>
    </div>
  );
};

export default OngoingProjectCard;